import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { imageSrcSet } from "@/data/location-images"
import { cdnUrl } from "@/lib/cdn"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Showcase properties                                                 */
/* ------------------------------------------------------------------ */

const SHOWCASE = [
  {
    image: "/property-royal-york-800w.webp",
    address: "8 Royal York Crescent",
    price: "£1,250,000",
    beds: 5,
    template: "Prestige",
    headline: "Georgian grandeur on Europe's longest crescent",
  },
  {
    image: "/property-canynge-square-800w.webp",
    address: "12 Canynge Square",
    price: "£575,000",
    beds: 2,
    template: "Modern",
    headline: "Contemporary living on the square",
  },
  {
    image: "/property-sion-hill-800w.webp",
    address: "4 Sion Hill",
    price: "£225/night",
    beds: 3,
    template: "Classic",
    headline: "Wake up to views of the Avon Gorge",
  },
]

/* ------------------------------------------------------------------ */
/* Phone mockup                                                        */
/* ------------------------------------------------------------------ */

function PhoneMockup({
  property,
  className = "",
}: {
  property: (typeof SHOWCASE)[number]
  className?: string
}) {
  return (
    <div className={`group relative mx-auto w-full max-w-[15rem] ${className}`}>
      <div className="relative overflow-hidden rounded-[2rem] border-[6px] border-charcoal bg-charcoal shadow-xl transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl">
        <div className="absolute left-1/2 top-0 z-20 h-5 w-24 -translate-x-1/2 rounded-b-xl bg-charcoal" />
        <div className="relative aspect-[9/16] overflow-hidden bg-charcoal">
          <OptimizedImage
            src={cdnUrl(property.image)}
            srcSet={imageSrcSet(property.image)}
            sizes="240px"
            alt={property.address}
            className="absolute inset-0 h-full w-full object-cover"
            width={800}
            height={1422}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute left-3 top-8 rounded bg-red px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
            {property.template}
          </div>
          <div className="absolute inset-x-0 bottom-0 p-4">
            <p className="text-xs font-medium text-white/70">{property.headline}</p>
            <p className="mt-1 font-display text-sm font-bold text-white">{property.address}</p>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-sm font-bold text-white">{property.price}</span>
              <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-medium text-white">
                {property.beds} bed
              </span>
            </div>
            <div className="mt-3 flex items-center justify-center">
              <div className="flex size-8 items-center justify-center rounded-full bg-white/30 backdrop-blur-sm">
                <svg viewBox="0 0 24 24" fill="white" className="ml-0.5 size-4">
                  <path d="M8 5.14v14l11-7-11-7z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Instruction Calculator graphic                                      */
/* ------------------------------------------------------------------ */

function InstructionCalculator() {
  return (
    <div className="relative w-full">
      <div className="absolute -inset-10 rounded-[2rem] bg-gradient-to-br from-red/8 via-transparent to-red/4 blur-3xl" />

      <div className="relative overflow-hidden rounded-2xl border border-stone/80 bg-white shadow-2xl shadow-charcoal/8">
        {/* Toolbar */}
        <div className="flex items-center justify-between border-b border-stone bg-cream/60 px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <div className="size-2.5 rounded-full bg-red/60" />
              <div className="size-2.5 rounded-full bg-gold/60" />
              <div className="size-2.5 rounded-full bg-green-500/50" />
            </div>
            <span className="ml-2 text-[11px] font-medium text-charcoal">
              Instruction Revenue Calculator
            </span>
          </div>
          <div className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5">
            <div className="size-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[10px] font-medium text-green-700">
              Live
            </span>
          </div>
        </div>

        {/* Instruction bar chart */}
        <div className="px-5 pt-4 pb-2">
          <p className="text-xs font-semibold text-charcoal">
            Extra instructions won with video
          </p>

          <svg viewBox="0 0 340 130" fill="none" className="mt-3 w-full">
            {/* Y-axis labels */}
            <text x="0" y="20" fontSize="8" fill="#9A9590">
              £15k
            </text>
            <text x="0" y="50" fontSize="8" fill="#9A9590">
              £10k
            </text>
            <text x="0" y="80" fontSize="8" fill="#9A9590">
              £5k
            </text>
            <text x="0" y="110" fontSize="8" fill="#9A9590">
              £0
            </text>

            {/* Grid lines */}
            {[16, 46, 76, 106].map((y) => (
              <line
                key={y}
                x1="30"
                y1={y}
                x2="340"
                y2={y}
                stroke="#E8E4DF"
                strokeWidth="0.5"
                strokeDasharray="4 4"
              />
            ))}

            {/* Instruction bars */}
            {[
              { label: "+1", x: 62, fees: 3000, colour: "#C41E2A", opacity: 0.4 },
              { label: "+2", x: 122, fees: 6000, colour: "#C41E2A", opacity: 0.6 },
              { label: "+3", x: 182, fees: 9000, colour: "#C41E2A", opacity: 0.8 },
              { label: "+5", x: 242, fees: 15000, colour: "#C41E2A", opacity: 1 },
            ].map((bar) => {
              const maxVal = 15000
              const barH = 90
              const height = (bar.fees / maxVal) * barH
              return (
                <g key={bar.label}>
                  <rect
                    x={bar.x - 20}
                    y={106 - height}
                    width="40"
                    height={height}
                    rx="4"
                    fill={bar.colour}
                    opacity={bar.opacity}
                  />
                  {/* Fee label above bar */}
                  <text
                    x={bar.x}
                    y={100 - height}
                    fontSize="9"
                    fill="#C41E2A"
                    textAnchor="middle"
                    fontWeight="700"
                  >
                    £{(bar.fees / 1000).toFixed(0)}k
                  </text>
                  {/* Instruction count below */}
                  <text
                    x={bar.x}
                    y="122"
                    fontSize="9"
                    fill="#9A9590"
                    textAnchor="middle"
                    fontWeight="600"
                  >
                    {bar.label}
                  </text>
                </g>
              )
            })}

            {/* QuiteRight cost line */}
            <line
              x1="30"
              y1={106 - (3588 / 15000) * 90}
              x2="280"
              y2={106 - (3588 / 15000) * 90}
              stroke="#16a34a"
              strokeWidth="1"
              strokeDasharray="6 3"
            />
            <rect
              x="282"
              y={106 - (3588 / 15000) * 90 - 8}
              width="58"
              height="16"
              rx="3"
              fill="#16a34a"
            />
            <text
              x="311"
              y={106 - (3588 / 15000) * 90 + 2}
              fontSize="7"
              fill="white"
              textAnchor="middle"
              fontWeight="600"
            >
              Cost: £3,588/yr
            </text>

            <text x="75" y="130" fontSize="7" fill="#9A9590">
              Extra instructions per year
            </text>
          </svg>
        </div>

        {/* Bottom metrics */}
        <div className="grid grid-cols-3 divide-x divide-stone border-t border-stone">
          {[
            { label: "Avg. fee per instruction", value: "£3,000", sub: "1.2% on £250k" },
            { label: "Break-even point", value: "2", sub: "instructions/year" },
            { label: "At 3 extra instructions", value: "+£5,412", sub: "net profit", accent: true },
          ].map((m) => (
            <div key={m.label} className="px-4 py-3">
              <p className="text-[10px] font-medium text-warm-grey">{m.label}</p>
              <p className={`mt-0.5 font-display text-lg font-bold tracking-tight ${m.accent ? "text-green-600" : "text-charcoal"}`}>
                {m.value}
              </p>
              <p className="text-[9px] text-warm-grey">{m.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Revenue growth graphic                                              */
/* ------------------------------------------------------------------ */

function RevenueGrowthChart() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-stone/80 bg-white shadow-xl shadow-charcoal/8">
      <div className="flex items-center justify-between border-b border-stone bg-cream/60 px-5 py-3">
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <div className="size-2.5 rounded-full bg-red/60" />
            <div className="size-2.5 rounded-full bg-gold/60" />
            <div className="size-2.5 rounded-full bg-green-500/50" />
          </div>
          <span className="ml-2 text-[11px] font-medium text-charcoal">
            Instruction revenue - 12-month model
          </span>
        </div>
      </div>

      <div className="px-5 pt-4 pb-2">
        <svg viewBox="0 0 340 150" fill="none" className="w-full" overflow="visible">
          {/* Grid */}
          {[30, 60, 90, 120].map((y) => (
            <line
              key={y}
              x1="30"
              y1={y}
              x2="320"
              y2={y}
              stroke="#E8E4DF"
              strokeWidth="0.5"
              strokeDasharray="4 4"
            />
          ))}

          {/* Y labels */}
          <text x="0" y="33" fontSize="7" fill="#9A9590">
            £20k
          </text>
          <text x="0" y="63" fontSize="7" fill="#9A9590">
            £15k
          </text>
          <text x="0" y="93" fontSize="7" fill="#9A9590">
            £10k
          </text>
          <text x="0" y="123" fontSize="7" fill="#9A9590">
            £5k
          </text>

          {/* Without video — flat line */}
          <path
            d="M40,95 C80,94 120,93.5 170,93 C220,92 270,91 310,90"
            stroke="#D4D0CB"
            strokeWidth="2"
            strokeLinecap="round"
          />
          {[
            [40, 95],
            [95, 94],
            [150, 93],
            [205, 92],
            [260, 91],
            [310, 90],
          ].map(([cx, cy], i) => (
            <circle
              key={`wo-${i}`}
              cx={cx}
              cy={cy}
              r="2.5"
              fill="white"
              stroke="#D4D0CB"
              strokeWidth="1.5"
            />
          ))}

          {/* With video — upward curve */}
          <path
            d="M40,95 C65,88 90,80 125,68 C160,56 195,44 235,34 C265,27 290,22 310,20"
            stroke="#C41E2A"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <path
            d="M40,95 C65,88 90,80 125,68 C160,56 195,44 235,34 C265,27 290,22 310,20 L310,135 L40,135 Z"
            fill="url(#revenueGrad)"
          />
          {[
            [40, 95],
            [95, 78],
            [150, 62],
            [205, 44],
            [260, 32],
            [310, 20],
          ].map(([cx, cy], i) => (
            <circle
              key={`wv-${i}`}
              cx={cx}
              cy={cy}
              r="3"
              fill="white"
              stroke="#C41E2A"
              strokeWidth="2"
            />
          ))}

          {/* Endpoint ring */}
          <circle
            cx="310"
            cy="20"
            r="7"
            fill="none"
            stroke="#C41E2A"
            strokeWidth="1"
            opacity="0.25"
          />

          {/* Gap annotation */}
          <line
            x1="285"
            y1="26"
            x2="285"
            y2="89"
            stroke="#C41E2A"
            strokeWidth="1"
            strokeDasharray="3 2"
            opacity="0.4"
          />
          <rect x="254" y="48" width="62" height="18" rx="4" fill="#C41E2A" />
          <text
            x="285"
            y="60"
            fontSize="9"
            fill="white"
            textAnchor="middle"
            fontWeight="600"
          >
            +3 instructions
          </text>

          {/* Legend */}
          <text x="155" y="14" fontSize="8" fill="#D4D0CB" fontWeight="600">
            ● Photos only
          </text>
          <text x="245" y="14" fontSize="8" fill="#C41E2A" fontWeight="600">
            ● With video
          </text>

          <defs>
            <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C41E2A" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#C41E2A" stopOpacity="0.01" />
            </linearGradient>
          </defs>
        </svg>

        {/* X-axis */}
        <div className="flex justify-between px-4 text-[9px] text-warm-grey">
          <span>Month 1</span>
          <span>Month 3</span>
          <span>Month 6</span>
          <span>Month 9</span>
          <span>Month 12</span>
        </div>
      </div>

      {/* Bottom metrics */}
      <div className="grid grid-cols-3 divide-x divide-stone border-t border-stone">
        {[
          { label: "Extra instructions", value: "3", sub: "per year (conservative)" },
          { label: "Additional fees earned", value: "£9,000", sub: "at avg. £3k per instruction" },
          { label: "Net profit after QuiteRight", value: "+£5,412", sub: "that's 2.5x return" },
        ].map((m) => (
          <div key={m.label} className="px-4 py-3">
            <p className="text-[10px] font-medium text-warm-grey">{m.label}</p>
            <p className="mt-0.5 font-display text-lg font-bold tracking-tight text-charcoal">
              {m.value}
            </p>
            <p className="text-[9px] text-warm-grey">{m.sub}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* FAQ data                                                            */
/* ------------------------------------------------------------------ */

const VALUE_FAQ = [
  {
    q: "How does video actually help me win instructions?",
    a: "Walk into a market appraisal and show the vendor a polished video of a comparable property. While other agents hand over a PDF, you're showing them exactly what their marketing will look like on Instagram, Facebook, and Rightmove. 73% of vendors say they'd choose the agent who offers video. It's the difference between getting a nod and getting the signature.",
  },
  {
    q: "What if I only win two extra instructions a year?",
    a: "Two extra instructions at the average fee of £3,000 each means £6,000 in additional revenue. QuiteRight costs £3,588 per year on the Managed plan. That's £2,412 in pure profit - and you've only won two. Win three and your profit jumps to £5,412. Every instruction after that is almost entirely profit.",
  },
  {
    q: "Do vendors really care about video marketing?",
    a: "40% of vendors say video directly influenced which agent they instructed. They may not ask for video by name, but when one agent shows them a professional video and the other shows a few photos, the decision is obvious. Vendors want to know their property will be marketed properly.",
  },
  {
    q: "We already win most of our market appraisals. Why do we need this?",
    a: "If you're already winning most, video helps you win the rest - and win them at a higher fee. Agents who market with video are perceived as premium. Vendors are less likely to negotiate your commission, more likely to accept your valuation, and more likely to refer you. It's about margins, not just volume.",
  },
  {
    q: "How quickly will I see results?",
    a: "Most agents use their first video in a market appraisal within the first week. The reaction from vendors is immediate - they can see what their marketing will look like before they've even instructed you. Whether that converts next week or next month depends on your pipeline, but the impact on vendor perception is instant.",
  },
  {
    q: "Will this work for my type of property?",
    a: "QuiteRight works for every listing - from studio flats to country estates, rentals to holiday lets. The templates adapt to the property. Premium properties get the prestige treatment. Standard listings get clean, professional marketing that makes them stand out. Every listing deserves video, not just the best ones.",
  },
  {
    q: "What if we're in a less competitive area?",
    a: "That's actually where video has the biggest impact. In competitive markets, video is becoming expected. In quieter markets, you'll be the only agent offering it. When a vendor compares two agents - one with professional video marketing, one without - the difference is impossible to ignore.",
  },
  {
    q: "Can I cancel if it doesn't help us win more instructions?",
    a: "Yes. Month-to-month billing, no contracts, no exit fees. Try it for 14 days free - no credit card required. If you don't see the impact in your market appraisals, you've lost nothing.",
  },
]

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function WhyQuiteRight({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        {/* Structured Data - FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: VALUE_FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
      </Head>

      {/* ============================================================= */}
      {/* HERO — Split: copy left, instruction calculator right          */}
      {/* ============================================================= */}
      <section className="overflow-hidden px-6 py-20 md:py-28">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col items-center gap-12 md:flex-row md:gap-16 lg:gap-20">
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Why QuiteRight
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
                Win the instructions your competitors can&rsquo;t<span className="text-red">.</span>
              </h1>
              <p className="mx-auto mt-6 max-w-lg text-lg leading-relaxed text-slate md:mx-0">
                Vendors choose agents who market properly. Show them a polished
                video of their property before they&rsquo;ve even instructed
                you - and watch the conversion rate change.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row md:items-start">
                <Link
                  href="/demo"
                  className="inline-flex w-full items-center justify-center rounded-md bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
                >
                  See it with your listings
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-7 py-3.5 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
                >
                  Request a demo
                </Link>
              </div>
              <p className="mt-5 text-sm text-warm-grey">
                14-day free trial &middot; No credit card &middot; Cancel any
                time
              </p>
            </div>

            <div className="relative w-full max-w-xs shrink-0 sm:max-w-sm md:max-w-md">
              <InstructionCalculator />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* THE REALITY — Why vendors choose agents with video             */}
      {/* ============================================================= */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <ScrollReveal className="mx-auto max-w-5xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            The reality
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold text-cream md:text-4xl">
            The agents winning instructions in 2026 all have one thing in
            common<span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-warm-grey">
            They show vendors what their marketing will look like - before the
            instruction is signed. Video is the tool that makes that possible.
          </p>

          <ScrollReveal
            stagger
            className="mt-14 grid gap-6 sm:grid-cols-3"
          >
            {[
              {
                stat: "73%",
                label:
                  "of vendors would choose the agent who offers video marketing",
              },
              {
                stat: "2x",
                label:
                  "more enquiries on listings marketed with video",
              },
              {
                stat: "40%",
                label:
                  "of vendors say video influenced which agent they instructed",
              },
            ].map((item) => (
              <div
                key={item.stat}
                className="rounded-xl border border-slate/30 bg-slate/10 p-6"
              >
                <p className="font-display text-3xl font-bold text-cream md:text-4xl">
                  {item.stat}
                </p>
                <p className="mt-2 text-sm leading-snug text-warm-grey">
                  {item.label}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </ScrollReveal>
      </section>

      {/* ============================================================= */}
      {/* HOW YOU WIN — Three ways video wins instructions               */}
      {/* ============================================================= */}
      <section className="border-t border-stone bg-white px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <ScrollReveal className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              How you win
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold text-charcoal md:text-4xl">
              Three ways video wins you instructions<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-slate">
              Every instruction is worth £3,000+ in fees. Here&rsquo;s how video
              helps you win more of them.
            </p>
          </ScrollReveal>

          <div className="mt-14 flex flex-col items-start gap-12 lg:flex-row lg:gap-16">
            {/* Left: cards */}
            <div className="flex-1 space-y-6">
              <ScrollReveal stagger className="space-y-6">
                {[
                  {
                    title: "Win at the market appraisal",
                    description:
                      "Walk in with a polished video of a comparable property. While the other agent shows a Rightmove PDF, you're showing the vendor exactly what their home will look like on Instagram and Facebook. That's what gets the signature.",
                    metric: "73% of vendors prefer the agent who offers video",
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0 1 16.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.023 6.023 0 0 1-2.77.852m0 0a17.867 17.867 0 0 1-1 3.172m1-3.172a6.022 6.022 0 0 1-2.77-.852" />
                      </svg>
                    ),
                  },
                  {
                    title: "Sell faster, earn more per year",
                    description:
                      "Video generates 2x more enquiries. More enquiries means more viewings, faster offers, and quicker completions. The faster you sell, the more instructions you can take on - and the more fees you earn per year.",
                    metric: "2x more enquiries per listing with video",
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
                      </svg>
                    ),
                  },
                  {
                    title: "Command higher fees without negotiation",
                    description:
                      "Agents who market with video are perceived as premium. Vendors who see professional marketing are less likely to haggle on your commission, more likely to accept your valuation, and more likely to refer you to neighbours, friends, and family.",
                    metric: "40% of vendors say video influenced their choice of agent",
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
                      </svg>
                    ),
                  },
                ].map((card) => (
                  <div
                    key={card.title}
                    className="rounded-xl border border-stone bg-cream p-6"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-red/10 text-red">
                        {card.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display text-lg font-bold text-charcoal">
                          {card.title}
                        </h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-slate">
                          {card.description}
                        </p>
                        <div className="mt-3 rounded-md border border-red/10 bg-red/5 px-3 py-1.5">
                          <p className="text-xs font-medium text-red">
                            {card.metric}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </ScrollReveal>
            </div>

            {/* Right: revenue chart graphic */}
            <ScrollReveal className="w-full shrink-0 lg:w-[45%]">
              <RevenueGrowthChart />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* TIME SAVED — More time for market appraisals                   */}
      {/* ============================================================= */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Time you get back
            </p>
            <h2 className="mt-3 font-display text-2xl font-bold text-cream md:text-3xl">
              Less time on marketing. More time winning instructions<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-warm-grey">
              Every hour you spend briefing videographers, chasing edits, and
              coordinating shoots is an hour you&rsquo;re not spending on market
              appraisals. QuiteRight gives you that time back.
            </p>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4">
            {[
              {
                value: "60s",
                label: "to create a video",
                sub: "vs 3–5 days with a videographer",
              },
              {
                value: "0",
                label: "coordination needed",
                sub: "No bookings, no briefs, no revisions",
              },
              {
                value: "50",
                label: "videos per month",
                sub: "Every listing gets video, automatically",
              },
              {
                value: "10+",
                label: "hours saved per month",
                sub: "Spent winning instructions instead",
              },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-charcoal px-5 py-5 text-center"
              >
                <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                  {item.value}
                </p>
                <p className="mt-1 text-xs font-medium text-cream/80">
                  {item.label}
                </p>
                <p className="mt-1 text-[10px] text-warm-grey">
                  {item.sub}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ============================================================= */}
      {/* THE WEAPON — What you show at market appraisals                */}
      {/* ============================================================= */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Your secret weapon
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold text-charcoal md:text-4xl">
              This is what you show at the market appraisal<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-slate">
              While other agents talk about what they&rsquo;ll do, you show the
              vendor what their property will actually look like. Paste a
              listing, choose a template, and present a finished video in
              seconds.
            </p>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-14 grid grid-cols-2 gap-6 sm:gap-8 lg:grid-cols-3">
            {SHOWCASE.map((property) => (
              <PhoneMockup key={property.address} property={property} />
            ))}
          </ScrollReveal>

          <ScrollReveal className="mt-12 text-center">
            <p className="mx-auto max-w-lg text-sm leading-relaxed text-slate">
              Imagine sitting across from a vendor, pulling up their property on
              your phone, and showing them a professional video ad - before
              they&rsquo;ve even signed. That&rsquo;s the moment you win the
              instruction.
            </p>
            <Link
              href="/examples"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-red transition-colors hover:text-deep-red"
            >
              See more examples
              <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
                <path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd" />
              </svg>
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* ============================================================= */}
      {/* THE MATHS — Instructions → Revenue                            */}
      {/* ============================================================= */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              The maths
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold text-cream md:text-4xl">
              Every extra instruction is worth thousands<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal className="mt-12">
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  label: "1 extra instruction",
                  value: "£3,000",
                  sub: "in additional fees",
                },
                {
                  label: "2 extra instructions",
                  value: "£6,000",
                  sub: "in additional fees",
                },
                {
                  label: "3 extra instructions",
                  value: "£9,000",
                  sub: "in additional fees",
                  accent: true,
                },
                {
                  label: "5 extra instructions",
                  value: "£15,000",
                  sub: "in additional fees",
                  accent: true,
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="bg-charcoal px-6 py-6 text-center"
                >
                  <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-warm-grey">
                    {item.label}
                  </p>
                  <p
                    className={`mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl ${
                      item.accent ? "text-red" : "text-cream"
                    }`}
                  >
                    {item.value}
                  </p>
                  <p className="mt-1 text-xs text-warm-grey">{item.sub}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>

          <ScrollReveal className="mt-8 text-center">
            <p className="text-sm leading-relaxed text-warm-grey">
              The average estate agent fee in England is 1.2% of the sale price.
              On a £250,000 property, that&rsquo;s{" "}
              <strong className="text-cream">£3,000</strong>. QuiteRight&rsquo;s
              Managed plan costs{" "}
              <strong className="text-cream">£299/month</strong>. Win just two
              extra instructions per year and you&rsquo;re{" "}
              <strong className="text-cream">£2,412 in profit</strong>. Win
              five and you&rsquo;ve added{" "}
              <strong className="text-cream">£11,412</strong> to your bottom
              line. The question isn&rsquo;t whether video pays for itself - it&rsquo;s
              how many extra instructions you&rsquo;ll win.{" "}
              <Link href="/tools/instruction-calculator" className="text-cream underline hover:text-red">
                Try our instruction calculator
              </Link>{" "}
              to model your own numbers.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ============================================================= */}
      {/* WHO IT'S FOR — Agent personas                                  */}
      {/* ============================================================= */}
      <section className="border-t border-stone bg-white px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Built for you
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold text-charcoal md:text-4xl">
              Whatever kind of agent you are<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "Independent agents",
                text: "Walk into market appraisals with the same quality marketing as the big corporates. Video levels the playing field - vendors judge you on the marketing, not the brand above the door.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                  </svg>
                ),
              },
              {
                title: "Multi-branch agencies",
                text: "Give every branch the same instruction-winning edge. Consistent video marketing across all offices means every negotiator walks into every appraisal armed with the same advantage.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                  </svg>
                ),
              },
              {
                title: "Letting agents",
                text: "Win landlord instructions with video that shows how you'll market their property. Fewer void periods, happier landlords, more management contracts.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
                  </svg>
                ),
              },
              {
                title: "Holiday let owners",
                text: "Video sells the experience, not just the property. Drive direct bookings, cut platform commissions, and fill more weeks per year.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                  </svg>
                ),
              },
            ].map((persona) => (
              <div
                key={persona.title}
                className="rounded-xl border border-stone bg-cream p-5"
              >
                <div className="flex size-10 items-center justify-center rounded-lg bg-red/10 text-red">
                  {persona.icon}
                </div>
                <h3 className="mt-3 font-display text-base font-bold text-charcoal">
                  {persona.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate">
                  {persona.text}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ============================================================= */}
      {/* FAQ                                                            */}
      {/* ============================================================= */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Questions agents ask before signing up<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate">
              Honest answers about winning instructions with video.
            </p>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {VALUE_FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-warm-grey">
              Have a different question?{" "}
              <Link href="/faq" className="text-red hover:text-deep-red">
                See all FAQs
              </Link>{" "}
              or{" "}
              <Link href="/contact" className="text-red hover:text-deep-red">
                get in touch
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* Industry context — outgoing authority links                     */}
      {/* ============================================================= */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center font-display text-2xl font-bold text-charcoal md:text-3xl">
            The UK property industry is going digital<span className="text-red">.</span>
          </h2>
          <div className="mx-auto mt-6 max-w-3xl space-y-4 text-sm leading-relaxed text-slate">
            <p>
              The shift towards digital marketing in the UK property industry is accelerating. The
              major portals,{" "}
              <a href="https://www.rightmove.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red">
                Rightmove
              </a>
              ,{" "}
              <a href="https://www.zoopla.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red">
                Zoopla
              </a>
              , and{" "}
              <a href="https://www.onthemarket.com" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red">
                OnTheMarket
              </a>
              {" "}, continue to invest in richer media formats. Social media platforms including{" "}
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red">
                Instagram
              </a>
              ,{" "}
              <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red">
                TikTok
              </a>
              , and{" "}
              <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red">
                YouTube
              </a>
              {" "}prioritise short-form video in their algorithms, giving video content significantly
              more organic reach than static images.
            </p>
            <p>
              Industry bodies like{" "}
              <a href="https://www.propertymark.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="Propertymark, professional body for property agents">
                Propertymark
              </a>
              {" "}and{" "}
              <a href="https://www.naea.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="National Association of Estate Agents">
                the NAEA
              </a>
              {" "}recognise that agents who adopt new marketing technologies are better positioned
              to win instructions and deliver results for vendors. Property video marketing isn&rsquo;t
              a luxury any more. It&rsquo;s table stakes for the modern estate agent.
            </p>
            <p>
              QuiteRight makes professional video marketing accessible to every UK estate agent,
              regardless of budget or technical ability. No filming, no editing, no marketing team
              required. Just paste a listing URL and publish a video ad that fills your viewing diary.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* FINAL CTA                                                      */}
      {/* ============================================================= */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            The agent with video wins the instruction<span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Make sure that agent is you. Start your free trial - no credit card,
            no commitment, no risk.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-8 py-4 text-lg font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              Book a 15-minute walkthrough
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; See it working with your listings
            &middot; No obligation
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
